/**
 * Curriculum Level Registry — what NZC level each (year, term, subject) is
 * actually pitched at, so families are told when a year reaches beyond itself.
 *
 * WHY THIS EXISTS
 * Years 5, 6 and 7 were each scoped as a "step up" from the previous NestWise
 * year rather than against the Ministry's expectations for that year. Every
 * year still covers its own official expectations, but several reach a full
 * NZC level beyond — Year 6 does so in every week of every term. That reach is
 * a feature of a supplementary resource; leaving it undeclared is not.
 *
 * NZC level ↔ year mapping: L1 Y1-2, L2 Y3-4, L3 Y5-6, L4 Y7-8, L5 Y9-10.
 * So the baseline is L3 for Years 5-6 and L4 for Year 7.
 *
 * SOURCE OF TRUTH: `backend/curriculum_check.py` prints this table from the
 * `nzCurriculumLink` of every shipped lesson. If that output changes, update
 * this file to match. Policy: `docs/curriculum/curriculum-level-policy.md`.
 *
 * GRANULARITY. Year 5 and Year 6 drift uniformly across whole terms, so they
 * are disclosed here, once, where the year is chosen. Year 7 is mixed within
 * terms — its Level 5 outliers (Pythagoras, box plots, scatter graphs, index
 * laws, Venn diagrams) are signposted individually in the lesson title or the
 * week's unit name instead, which is more precise. That is why Year 7 returns
 * no note here: it is already labelled, not overlooked.
 */

// Measured level per (year → term → subject). Baseline is what the year
// SHOULD be; anything above it is reach that needs declaring.
const LEVELS = {
  5: {
    baseline: 3,
    terms: {
      1: { maths: 3, english: 3 },
      2: { maths: 3, english: 3 },
      3: { maths: 4, english: 3 },
      4: { maths: 4, english: 3 },
    },
  },
  6: {
    baseline: 3,
    terms: {
      1: { maths: 4, english: 4 },
      2: { maths: 4, english: 4 },
      3: { maths: 4, english: 4 },
      4: { maths: 4, english: 4 },
    },
  },
};

// Years whose above-level content is signposted per lesson/week instead.
const SIGNPOSTED_PER_LESSON = [7];

const SUBJECT_LABEL = { maths: 'Maths', english: 'English' };

/** The school years a given NZC level is normally taught in. */
export function yearsForLevel(level) {
  const spans = { 1: 'Years 1-2', 2: 'Years 3-4', 3: 'Years 5-6', 4: 'Years 7-8', 5: 'Years 9-10' };
  return spans[level] || `Level ${level}`;
}

/**
 * The level note for a (year, term), or null when nothing needs declaring.
 * Returns { level, baseline, headline, detail, subjects } where `subjects`
 * lists the subjects that are above baseline.
 */
export function getLevelNote(year, term) {
  if (SIGNPOSTED_PER_LESSON.includes(Number(year))) return null;

  const entry = LEVELS[year];
  if (!entry) return null;
  const termLevels = entry.terms[term];
  if (!termLevels) return null;

  const above = Object.entries(termLevels)
    .filter(([, level]) => level > entry.baseline)
    .map(([subject]) => subject);
  if (above.length === 0) return null;

  const level = Math.max(...above.map(s => termLevels[s]));
  const onLevel = Object.keys(termLevels).filter(s => !above.includes(s));
  const abovePhrase = above.map(s => SUBJECT_LABEL[s]).join(' and ');

  // Agreement: one subject takes "is/works/It covers", two take "are/work/They cover".
  const plural = above.length > 1;
  const headline = plural
    ? `Pitched a level above Year ${year}`
    : `${abovePhrase} is pitched a level above Year ${year}`;

  let detail =
    `${abovePhrase} in Year ${year} Term ${term} ${plural ? 'work' : 'works'} at ` +
    `NZC Level ${level} (${yearsForLevel(level)}) — one level above the Level ` +
    `${entry.baseline} baseline for Year ${year}. ${plural ? 'They cover' : 'It covers'} ` +
    `all the Ministry's expectations for Year ${year}, and ${plural ? 'reach' : 'reaches'} beyond them.`;

  if (onLevel.length > 0) {
    const restPhrase = onLevel.map(s => SUBJECT_LABEL[s]).join(' and ');
    detail += ` ${restPhrase} ${onLevel.length > 1 ? 'stay' : 'stays'} at Level ${entry.baseline}.`;
  }

  return { level, baseline: entry.baseline, headline, detail, subjects: above };
}

export default getLevelNote;
